<template>
    <div class="roteirizacao-turnos-raios">
        <merus-data-table
            :headers="headers"
            :items="rows"
            :loading="loading"
            item-key="_rowKey"
            :items-per-page="-1"
            hide-sort-button
            flat
        >
            <template v-slot:toolbar>
                <div class="d-flex align-center flex-wrap px-3 pt-2">
                    <v-chip v-if="fonte" small label class="mr-2">{{ fonteText }}</v-chip>
                    <v-spacer />
                    <v-btn x-small text color="primary" :disabled="loading" @click="$emit('reload')">
                        <v-icon x-small left>mdi-restore</v-icon>
                        {{ $t('routePlanning.turnosRaios.reload') }}
                    </v-btn>
                    <v-btn x-small text color="primary" @click="addTurno">
                        <v-icon x-small left>mdi-plus</v-icon>
                        {{ $t('routePlanning.turnosRaios.add') }}
                    </v-btn>
                </div>
            </template>
            <template v-slot:item.cod_turno="{ item }">
                <v-text-field
                    :value="item.cod_turno"
                    dense
                    maxlength="50"
                    class="turnos-raios__input"
                    hide-details="auto"
                    :rules="nameRules(item._idx)"
                    @input="updateTurno(item._idx, { cod_turno: $event })"
                />
            </template>
            <template v-slot:item.inicio="{ item }">
                <v-text-field
                    :value="janela(item).inicio"
                    type="time"
                    dense
                    class="turnos-raios__input"
                    hide-details="auto"
                    :rules="timeRules"
                    @input="updateJanela(item._idx, 'inicio', $event)"
                />
            </template>
            <template v-slot:item.fim="{ item }">
                <v-text-field
                    :value="janela(item).fim"
                    type="time"
                    dense
                    class="turnos-raios__input"
                    hide-details="auto"
                    :rules="timeRules"
                    @input="updateJanela(item._idx, 'fim', $event)"
                />
            </template>
            <template v-for="col in radiusColumns" v-slot:[`item.${col.value}`]="{ item }">
                <v-text-field
                    :key="col.value"
                    :value="item[col.value]"
                    type="number"
                    min="1"
                    dense
                    suffix="m"
                    class="turnos-raios__input"
                    hide-details="auto"
                    :rules="radiusRules(item._idx, col)"
                    @input="updateTurno(item._idx, { [col.value]: $event === '' ? null : $event })"
                />
            </template>
            <template v-slot:item.actions="{ item }">
                <v-btn icon small :title="$t('routePlanning.turnosRaios.remove')" @click="removeTurno(item._idx)">
                    <v-icon small color="red">mdi-delete</v-icon>
                </v-btn>
            </template>
            <template v-slot:no-data>
                {{ $t('routePlanning.turnosRaios.empty') }}
            </template>
        </merus-data-table>
    </div>
</template>

<script>
import MerusDataTable from '@/components/Tables/MerusDataTable.vue';

const DAYS = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];

/**
 * Turnos (das rotas) usados na roteirização e os raios ideal/máximo de cada sentido.
 * Vêm do cadastro de Turnos (os da unidade do destino ou os gerais) e são editáveis só para
 * esta roteirização, como os turnos dos motoristas: "Recarregar turnos" volta ao cadastro.
 * Raio vazio usa o global de Configurações (placeholder).
 *
 * Início/fim editam a janela de todos os dias que o turno já tem preenchidos (turno novo:
 * a semana toda), porque é por esses horários que a parada casa com o turno.
 */
export default {
    name: 'RoteirizacaoTurnosRaios',
    components: { MerusDataTable },
    props: {
        /** Turnos da roteirização (`destinationShifts`). */
        value: {
            type: Array,
            default: () => [],
        },
        /** 'turnos_unidade' | 'turnos_gerais' (resposta de getturnosporcerca). */
        fonte: {
            type: String,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        /** Raios globais de Configurações (`{ ideal, max }`). */
        radiusConfig: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            timeRules: [
                (v) => /^\d{2}:\d{2}$/.test(String(v || '')) || this.$t('routePlanning.drivers.params.invalidTime'),
            ],
        };
    },
    computed: {
        headers() {
            const col = (value, text, width) => ({
                value, text, width, sortable: false, filtrable: false,
            });
            return [
                col('cod_turno', this.$t('routePlanning.turnosRaios.name'), '170px'),
                col('inicio', this.$t('routePlanning.turnosRaios.start'), '120px'),
                col('fim', this.$t('routePlanning.turnosRaios.end'), '120px'),
                ...this.radiusColumns.map((c) => col(c.value, c.text, '150px')),
            ];
        },
        /** Linhas da tabela: índice em `value` e chave estável (o objeto muda a cada edição). */
        rows() {
            return this.value.map((t, idx) => ({ ...t, _idx: idx, _rowKey: this.rowKey(t, idx) }));
        },
        radiusColumns() {
            return [
                { value: 'raio_ideal_ida', text: this.$t('turnos.headers.idealRadiusIda'), global: 'ideal', sentido: 'ida' },
                { value: 'raio_maximo_ida', text: this.$t('turnos.headers.maximumRadiusIda'), global: 'max', sentido: 'ida' },
                { value: 'raio_ideal_volta', text: this.$t('turnos.headers.idealRadiusVolta'), global: 'ideal', sentido: 'volta' },
                { value: 'raio_maximo_volta', text: this.$t('turnos.headers.maximumRadiusVolta'), global: 'max', sentido: 'volta' },
            ];
        },
        fonteText() {
            if (this.fonte === 'turnos_unidade') {
                const comUnidade = this.value.find((t) => t && t.cod_unidade);
                return this.$t('routePlanning.turnosRaios.sourceUnit', { unit: (comUnidade && comUnidade.cod_unidade) || '-' });
            }
            return this.$t('routePlanning.turnosRaios.sourceGeneral');
        },
    },
    methods: {
        isEmpty(v) {
            return v === null || v === undefined || String(v).trim() === '';
        },
        rowKey(t, idx) {
            if (t && t.id_turno != null) return `turno-${t.id_turno}`;
            return (t && t._sessionKey) || `turno-novo-${idx}`;
        },
        globalText(kind) {
            const value = Number(this.radiusConfig && this.radiusConfig[kind]);
            return Number.isFinite(value) && value > 0
                ? this.$t('routePlanning.turnosRaios.globalValue', { value })
                : '';
        },
        nameRules(idx) {
            return [
                (v) => !this.isEmpty(v) || this.$t('routePlanning.turnosRaios.nameRequired'),
                (v) => !this.value.some((t, i) => i !== idx
                    && String((t && t.cod_turno) || '').trim() === String(v || '').trim())
                    || this.$t('routePlanning.turnosRaios.nameDuplicated'),
            ];
        },
        radiusRules(idx, col) {
            return [
                (v) => this.isEmpty(v) || (Number.isFinite(Number(v)) && Number(v) > 0)
                    || this.$t('routePlanning.drivers.params.invalidNumber'),
                (v) => {
                    if (col.global !== 'ideal' || this.isEmpty(v)) return true;
                    const max = (this.value[idx] || {})[`raio_maximo_${col.sentido}`];
                    if (this.isEmpty(max)) return true;
                    return Number(v) <= Number(max) || this.$t('rules.messageRadiusIdealAboveMax');
                },
            ];
        },
        /** Janela exibida: a do primeiro dia preenchido. */
        janela(t) {
            for (const day of DAYS) {
                const inicio = t[`horario_inicio_${day}`];
                const fim = t[`horario_fim_${day}`];
                if (!this.isEmpty(inicio) || !this.isEmpty(fim)) {
                    return { inicio: inicio || '', fim: fim || '' };
                }
            }
            return { inicio: '', fim: '' };
        },
        emit(turnos) {
            this.$emit('input', turnos);
        },
        updateTurno(idx, patch) {
            this.emit(this.value.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
        },
        updateJanela(idx, campo, raw) {
            const t = this.value[idx] || {};
            const preenchidos = DAYS.filter((day) => !this.isEmpty(t[`horario_inicio_${day}`])
                || !this.isEmpty(t[`horario_fim_${day}`]));
            const dias = preenchidos.length ? preenchidos : DAYS;
            const patch = {};
            dias.forEach((day) => { patch[`horario_${campo}_${day}`] = raw || null; });
            this.updateTurno(idx, patch);
        },
        addTurno() {
            const novo = {
                id_turno: null,
                _sessionKey: `turno-novo-${Date.now()}`,
                cod_turno: `T${this.value.length + 1}`,
                descricao_turno: null,
                raio_ideal_ida: null,
                raio_maximo_ida: null,
                raio_ideal_volta: null,
                raio_maximo_volta: null,
            };
            DAYS.forEach((day) => {
                novo[`horario_inicio_${day}`] = null;
                novo[`horario_fim_${day}`] = null;
            });
            this.emit(this.value.concat([novo]));
        },
        removeTurno(idx) {
            this.emit(this.value.filter((_, i) => i !== idx));
        },
    },
};
</script>

<style scoped>
.turnos-raios__input {
    margin-top: 0;
    padding-top: 4px;
    padding-bottom: 4px;
    min-width: 90px;
}
</style>
